import { ArrowRight } from "lucide-react";
import ExternalLinkWarning from "@/components/ExternalLinkWarning";
import { Button } from "@/components/ui/button";
import { useQuestionnaire } from "@/components/questionnaire/QuestionnaireContext";
import { useLiveRates } from "@/hooks/use-live-rates";

const MarketRatesSection = () => {
  const { openQuestionnaire } = useQuestionnaire();
  const { data: rates } = useLiveRates();

  const rateCards = [
    { label: "30-Year Fixed", rate: `${rates?.thirtyYear}%` },
    { label: "15-Year Fixed", rate: `${rates?.fifteenYear}%` },
    { label: "FHA", rate: `${rates?.fha}%` },
    { label: "VA", rate: `${rates?.va}%` },
  ];

  return (
  <section className="bg-secondary border-y border-border">
    <div className="mx-auto max-w-[1440px] px-6 py-16 lg:px-10 lg:py-20">
      <div className="flex flex-col items-center text-center gap-2 mb-8">
        <h2 className="font-display text-2xl font-semibold text-foreground tracking-tight md:text-3xl">
          Today's Mortgage Snapshot
        </h2>
        <p className="font-body text-sm font-medium tracking-[0.04em] text-muted-foreground">
          Updated {rates?.asOf}
        </p>
      </div>

      {/* Rate cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 max-w-4xl mx-auto mb-10">
        {rateCards.map((r) => (
          <div
            key={r.label}
            className="group rounded-xl border border-border/80 bg-card px-5 py-6 text-center transition-all duration-300 hover:shadow-md hover:-translate-y-1 hover:border-accent"
          >
            <p className="font-body text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-2">
              {r.label}
            </p>
            <p className="font-display text-[2.1rem] md:text-[2.4rem] font-bold text-foreground leading-none mb-1.5">
              {r.rate}
            </p>
            <p className="font-body text-sm text-muted-foreground">
              Market Average
            </p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="flex justify-center mb-6">
        <Button variant="default" size="sm" onClick={openQuestionnaire} className="flex items-center gap-1.5">
          <span className="text-sm">See Your Personalized Rate</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Disclaimer */}
      <p className="font-body text-sm text-muted-foreground max-w-lg mx-auto text-center leading-relaxed">
        Market averages shown. The rate information is for illustrative purposes only and is not an offer; it may not reflect current rates. Additional terms are required to present an APR. <button onClick={openQuestionnaire} className="underline underline-offset-2 hover:text-foreground transition-colors">Please contact us</button> for additional terms and APR.
      </p>
      <p className="font-body text-sm text-disclosure max-w-lg mx-auto text-center leading-relaxed mt-2">
        30-Year and 15-Year Fixed rates sourced from the{" "}
        <ExternalLinkWarning href="https://www.freddiemac.com/pmms" className="underline hover:text-foreground">Freddie Mac Primary Mortgage Market Survey</ExternalLinkWarning>.
        FHA and VA rates sourced from{" "}
        <ExternalLinkWarning href="https://marketnewsletters.com" className="underline hover:text-foreground">Market Newsletters</ExternalLinkWarning>.
      </p>
    </div>
  </section>
  );
};

export default MarketRatesSection;
